import React, { useRef } from 'react';
import { Tooltip } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { formatDateAndTime, formatDateAndTimeFromNow } from '../../../helpers/utils';
import { getTagForChangeTypes, getTagForStates } from '../../Common/Tags';
import LazyExpandedRow from '../../Common/LazyExpandedRow';
import CollapsePanel from '../../Common/CollapsePanel';
import { useExpandedRow } from '../../Common/OpenCard';
import { CounterpartyName } from '../../Teams/Counterparty';
import { changeTypeColor, DEFAULT_CHANGE_TYPE_COLOR } from './constants';
import { ChangeInstancesListConfig } from './types';

/** one context column — the header above it carries the glyph and the name */
const ContextCol = ({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) => (
    <div className="ci-col">
        <Tooltip placement="topLeft" title={`${label}: ${value || '—'}`}>
            <span className="ci-col__value">{children ?? value ?? '—'}</span>
        </Tooltip>
    </div>
);

/**
 * A full-width entry that expands in place, so the side-by-side declaration
 * diff gets the whole page width. Rows sit flush against each other and take
 * their border colour from the change type.
 */
const ChangeEntry = ({
    record,
    open,
    onToggle,
    config,
    refetch,
    scrollIntoView,
}: {
    record: any;
    open: boolean;
    onToggle: () => void;
    config: ChangeInstancesListConfig;
    refetch: () => void;
    /** a link asked for this entry: bring it into view, once */
    scrollIntoView?: boolean;
}) => {
    const accent = changeTypeColor[record.change_type] || DEFAULT_CHANGE_TYPE_COLOR;
    const ref = useRef<HTMLDivElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const party = config.partyField.team(record);

    // opened — by a click or by a link — it follows the rules every row that
    // opens in place does: see useExpandedRow
    useExpandedRow(ref, barRef, open, { deepLinked: scrollIntoView });

    return (
        <div
            ref={ref}
            // anchor for a hash deep link; prefixed so the raw id can never
            // collide with an unrelated element
            id={`ci-entry-${record.id}`}
            className={`ci-entry${open ? ' ci-entry--open' : ''}`}
            style={{ ['--ci-accent' as any]: accent }}
        >
            <div
                ref={barRef}
                className="ci-entry__bar"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-label={`Change instance ${record.id}`}
                onClick={onToggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggle();
                    }
                }}
            >
                <span className="ci-entry__toggle" aria-hidden="true">
                    {open ? <MinusOutlined /> : <PlusOutlined />}
                </span>

                <span className="ci-entry__id">#{record.id}</span>

                {record.service_item?.id ? (
                    /* the bar is clickable, so this link must not bubble */
                    <Tooltip placement="topLeft" title={record.service_item?.name}>
                        <Link
                            className="ci-entry__name"
                            to={`${config.serviceItemPath}/${record.service_item.id}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {record.service_item.name}
                        </Link>
                    </Tooltip>
                ) : (
                    <span className="ci-entry__name">{record.service_item?.name || '—'}</span>
                )}

                {/* the same order the service items list reads in: whose it is,
                    what it belongs to, what it is — and the whose opens the
                    team behind the name */}
                <ContextCol label={config.partyField.label} value={party?.name}>
                    <CounterpartyName
                        team={party}
                        pov={config.partyField.pov}
                        label={config.partyField.label}
                    />
                </ContextCol>
                <ContextCol label="Application" value={record.application?.name} />
                <ContextCol label="Service" value={record.service?.name} />

                <span className="ci-entry__tags">
                    {getTagForChangeTypes(record.change_type)}
                    {getTagForStates(record.state)}
                </span>

                <span className="ci-entry__time">
                    {record.modified && (
                        <Tooltip title={`Updated ${formatDateAndTime(record.modified)}`}>
                            <span>{formatDateAndTimeFromNow(record.modified)}</span>
                        </Tooltip>
                    )}
                </span>
            </div>

            <CollapsePanel open={open} className="ci-entry__panel nx-open-row__panel">
                <LazyExpandedRow
                    lightRecord={record}
                    resource={config.resource}
                    ExpandedRowComponent={config.ExpandedRow}
                    // the detail panel needs it to load this change's history
                    extraProps={{ resource: config.resource }}
                    {...(config.expandedRowNeedsRefresh ? { refresh: refetch } : {})}
                />
            </CollapsePanel>
        </div>
    );
};

export default ChangeEntry;
